import { useState, useEffect, useRef, useCallback } from "react";
import { nanoid } from "nanoid";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { RoomStore } from "@/lib/room-store";
import { ScreenShare as ScreenShareService } from "@/lib/screen-share";
import VideoPlayer, { type FloatingReactionItem } from "@/components/video-player";
import ChatPanel, { type ChatMessage } from "@/components/chat-panel";
import FullscreenChatOverlay from "@/components/fullscreen-chat-overlay";
import VideoUrlBar from "@/components/video-url-bar";
import ParticipantBar from "@/components/participant-bar";
import YouTubeSearchModal from "@/components/youtube-browser-modal";
import LandingHero from "@/components/landing-hero";
import RecentRooms from "@/components/recent-rooms";
import NowPlayingPreview from "@/components/now-playing-preview";
import ThemeToggle from "@/components/theme-toggle";
import RoomSettingsModal from "@/components/room-settings-modal";
import HistoryPanel from "@/components/history-panel";
import AnalyticsPanel from "@/components/analytics-panel";
import { parseSubtitleFile } from "@/lib/subtitles";
import { formatTime } from "@/lib/video-utils";
import { REACTION_ORDER, REACTION_ICONS } from "@/components/reaction-icons";
import { getProfile, saveProfile } from "@/lib/profile-store";
import { getAvatarColor } from "@/lib/room-state";
import {
  Play,
  Pause,
  Users,
  Sparkles,
  Zap,
  Film,
  ListVideo,
  BarChart2,
  HelpCircle,
  X,
  Subtitles,
  Upload,
  ArrowLeft,
  Loader2,
  Monitor,
  Settings,
  ChevronUp,
  ChevronDown,
  ScreenShare,
  Square,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  RefreshCw,
  PanelsTopBottom,
  RectangleHorizontal,
  Layers,
  Radio,
} from "lucide-react";
import type {
  VideoState,
  VideoType,
  Participant,
  PlaylistItem,
  RoomPoll,
  VideoBookmark,
  SubtitleTrack,
  RoomEvent,
  ReactionIcon,
  TimeComment,
  HistoryEntry,
  CustomReaction,
  RoomAnalytics,
} from "@/lib/room-state";

type View = "landing" | "room";

interface RoomSession {
  roomId: string;
  userId: string;
  userName: string;
  isHost: boolean;
  // admin_id stored at create time — used for admin persistence on rejoin.
  // When the creator reopens the room URL, we compare this with the DB's
  // admin_id (delivered via the init event as `adminId`) to restore host
  // status. Optional: only set for the room's creator.
  adminId?: string;
}

export default function Home() {
  const [view, setView] = useState<View>("landing");
  const [session, setSession] = useState<RoomSession | null>(null);
  const [userName, setUserName] = useState("");
  const [roomTitle, setRoomTitle] = useState("");
  const [hostOnly, setHostOnly] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRoomId, setPendingRoomId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => {
      if (!active) return;
      const p = getProfile();
      if (p?.name) setUserName(p.name);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const room = params.get("room");
    if (room && room.length >= 3) {
      const savedName = sessionStorage.getItem(`room_${room}_userName`);
      const savedId = sessionStorage.getItem(`room_${room}_userId`);
      Promise.resolve().then(() => {
        if (savedName && savedId) {
          // Admin persistence: read the admin_id we stored at create time.
          // The RoomView will also double-check this against the DB's
          // admin_id (delivered via the init event) before trusting it.
          const savedAdminId = sessionStorage.getItem(`room_${room}_adminId`) || undefined;
          const savedIsHost = sessionStorage.getItem(`room_${room}_isHost`) === "true";
          // If we have a stored admin_id that matches our userId, we are the
          // room's creator — restore host status even if isHost got cleared.
          const isHost = savedIsHost || (!!savedAdminId && savedAdminId === savedId);
          setSession({ roomId: room, userId: savedId, userName: savedName, isHost, adminId: savedAdminId });
          setView("room");
        } else {
          setPendingRoomId(room);
        }
      });
    }
  }, []);

  function rememberRoom(roomId: string, title: string) {
    try {
      const raw = localStorage.getItem("syncscreen:recent_rooms");
      const list: { roomId: string; title: string; visitedAt: number }[] = raw ? JSON.parse(raw) : [];
      const filtered = list.filter((r) => r.roomId !== roomId);
      filtered.unshift({ roomId, title, visitedAt: Date.now() });
      localStorage.setItem("syncscreen:recent_rooms", JSON.stringify(filtered.slice(0, 5)));
    } catch {}
  }

  async function createRoom() {
    const finalName = userName.trim();
    if (!finalName) { setError("Please enter your display name."); return; }
    setLoading(true); setError(null);
    try {
      const data = await RoomStore.createRoom(finalName, hostOnly, roomTitle.trim() || "Watch Party Room");
      sessionStorage.setItem(`room_${data.roomId}_userId`, data.hostId);
      sessionStorage.setItem(`room_${data.roomId}_userName`, data.hostName);
      sessionStorage.setItem(`room_${data.roomId}_isHost`, "true");
      // Admin persistence: store the creator's admin_id (= hostId) so that on
      // a future rejoin we can restore host status even if isHost is cleared.
      sessionStorage.setItem(`room_${data.roomId}_adminId`, data.hostId);
      saveProfile(finalName);
      rememberRoom(data.roomId, data.title);
      setSession({ roomId: data.roomId, userId: data.hostId, userName: data.hostName, isHost: true, adminId: data.hostId });
      setView("room");
      toast.success("Watch room created", { description: `Room ${data.roomId} is live.` });
      const url = new URL(window.location.href);
      url.searchParams.set("room", data.roomId);
      window.history.replaceState({}, "", url);
    } catch {
      setError("Could not create the room. Please try again.");
      toast.error("Could not create the room");
      setLoading(false);
    }
  }

  function joinRoom() {
    const code = joinCode.trim();
    if (!code) { setError("Enter a room code or link."); return; }
    const match = code.match(/room(?:=|%3D|\/)([a-zA-Z0-9_-]+)/);
    const roomId = match ? match[1] : code.replace(/^.*\//, "");
    if (!roomId || roomId.length < 3) { setError("That doesn't look like a valid room code or link."); return; }
    setPendingRoomId(roomId);
  }

  function confirmJoinRoom(name: string) {
    if (!pendingRoomId || !name.trim()) return;
    const userId = nanoid(12);
    sessionStorage.setItem(`room_${pendingRoomId}_userId`, userId);
    sessionStorage.setItem(`room_${pendingRoomId}_userName`, name.trim());
    sessionStorage.setItem(`room_${pendingRoomId}_isHost`, "false");
    saveProfile(name.trim());
    rememberRoom(pendingRoomId, "Watch Party Room");
    setSession({ roomId: pendingRoomId, userId, userName: name.trim(), isHost: false });
    setPendingRoomId(null);
    setView("room");
    toast.success("Joined room", { description: `Room ${pendingRoomId}` });
    const url = new URL(window.location.href);
    url.searchParams.set("room", pendingRoomId);
    window.history.replaceState({}, "", url);
  }

  function rejoinRoom(roomId: string, _title: string) { setPendingRoomId(roomId); }

  function leaveRoom() {
    if (session) {
      sessionStorage.removeItem(`room_${session.roomId}_userId`);
      sessionStorage.removeItem(`room_${session.roomId}_userName`);
      sessionStorage.removeItem(`room_${session.roomId}_isHost`);
      sessionStorage.removeItem(`room_${session.roomId}_adminId`);
    }
    setSession(null); setView("landing");
    const url = new URL(window.location.href);
    url.searchParams.delete("room");
    window.history.replaceState({}, "", url);
  }

  if (view === "room" && session) return <RoomView session={session} onLeave={leaveRoom} />;

  return (
    <div className="min-h-screen flex flex-col relative text-foreground overflow-hidden selection:bg-[#78B4FF]/20">
      {/* Static atmospheric gradients behind the UI — barely visible */}
      <div aria-hidden className="atmosphere" />

      {/* Top nav — minimal, no glass pill clutter */}
      <header className="sticky top-0 z-20 border-b border-white/[0.06] backdrop-blur-xl bg-[#0B0D10]/55">
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 h-[56px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg glass-secondary">
              <Monitor className="w-4 h-4 text-[#78B4FF]" />
            </div>
            <span className="text-[13px] font-semibold tracking-tight">SyncScreen</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 relative z-10">
        <div className="w-full max-w-[1120px]">
          <div className="mx-auto max-w-3xl text-center">
            <LandingHero />
          </div>

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mx-auto max-w-2xl mt-6">
                <div className="glass-primary rounded-xl px-4 py-3 text-xs text-center text-[#78B4FF]">
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action grid — two floating glass-primary panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10 max-w-[860px] mx-auto">
            {/* Create */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
              className="glass-primary glass-highlight-edge rounded-3xl p-7 flex flex-col relative"
            >
              <div className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#78B4FF]/12">
                  <Sparkles className="w-4 h-4 text-[#78B4FF]" />
                </div>
                <div>
                  <h2 className="text-[13px] font-semibold tracking-tight">Start a Watch Party</h2>
                  <p className="text-[11px] text-muted-foreground">Create a room in one tap</p>
                </div>
              </div>

              <label className="block text-[10px] font-medium tracking-widest uppercase text-muted-foreground mb-1.5">Your display name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createRoom()}
                placeholder="e.g. Maya"
                maxLength={40}
                className="w-full glass-input rounded-xl px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none mb-4"
              />
              <label className="block text-[10px] font-medium tracking-widest uppercase text-muted-foreground mb-1.5">Room title — optional</label>
              <input
                type="text"
                value={roomTitle}
                onChange={(e) => setRoomTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createRoom()}
                placeholder="e.g. Movie Night — Studio Ghibli"
                maxLength={80}
                className="w-full glass-input rounded-xl px-3.5 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none mb-4"
              />
              <label className="flex items-center gap-2.5 glass-tertiary rounded-xl px-3 py-2.5 mb-5 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={hostOnly}
                    onChange={(e) => setHostOnly(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="w-[36px] h-[20px] rounded-full bg-white/[0.08] peer-checked:bg-[#78B4FF] transition-colors" />
                  <div className="absolute top-[3px] left-[3px] w-[14px] h-[14px] rounded-full bg-white shadow peer-checked:translate-x-[16px] transition-transform" />
                </div>
                <span className="text-xs font-medium">Host-only playback controls</span>
              </label>

              <button
                onClick={createRoom}
                disabled={loading || !userName.trim()}
                className="w-full glass-button-primary text-sm font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 mt-auto disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.99]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                {loading ? "Creating…" : "Create Watch Room"}
              </button>
              <p className="text-[10px] text-center text-muted-foreground mt-2.5">Shareable link generated instantly</p>
            </motion.div>

            {/* Join */}
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
              className="glass-primary glass-highlight-edge rounded-3xl p-7 flex flex-col relative"
            >
              <div className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#78B4FF]/12">
                  <Users className="w-4 h-4 text-[#78B4FF]" />
                </div>
                <div>
                  <h2 className="text-[13px] font-semibold tracking-tight">Join a Party</h2>
                  <p className="text-[11px] text-muted-foreground">Paste a code or link</p>
                </div>
              </div>

              <label className="block text-[10px] font-medium tracking-widest uppercase text-muted-foreground mb-1.5">Room link or code</label>
              <div className="relative mb-4 group">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && joinRoom()}
                  placeholder="Paste code or full link"
                  className="w-full glass-input rounded-xl pl-10 pr-3 py-3 text-sm font-mono text-foreground placeholder:text-muted-foreground/50 placeholder:font-sans outline-none"
                />
                <Layers className="w-4 h-4 text-muted-foreground/60 absolute left-3.5 top-1/2 -translate-y-1/2" />
              </div>
              <div className="glass-tertiary rounded-xl p-3.5 mb-5">
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  Paste a room link or code that someone shared. You’ll join instantly — no refresh, no reload.
                </p>
              </div>
              <button
                onClick={joinRoom}
                disabled={!joinCode.trim() || joinLoading}
                className="w-full glass-button-ghost text-sm font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 mt-auto transition-all active:scale-[0.99] disabled:opacity-40"
              >
                <Radio className="w-4 h-4" />
                Join with Code
              </button>
              <p className="text-[10px] text-center text-muted-foreground mt-2.5 flex items-center justify-center gap-1">
                <span className="w-1 h-1 rounded-full bg-[#78B4FF] animate-pulse" />
                Enter name on next step
              </p>
            </motion.div>
          </div>

          {/* Feature highlights — minimal glass-tertiary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-6 max-w-[860px] mx-auto">
            {[
              { icon: Zap, label: "Sub-second sync", desc: "Play / pause / seek" },
              { icon: Film, label: "Multi-source", desc: "YouTube • MP4 • HLS" },
              { icon: ListVideo, label: "Shared queue", desc: "Auto-play next" },
              { icon: BarChart2, label: "Live polls", desc: "Instant voting" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 + i * 0.05 }}
                className="glass-tertiary rounded-2xl p-4 text-center hover:bg-white/[0.05] transition-all"
              >
                <div className="flex h-9 w-9 mx-auto mb-2.5 items-center justify-center">
                  <item.icon className="w-4 h-4 text-[#78B4FF]" />
                </div>
                <div className="text-xs font-semibold">{item.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 max-w-[860px] mx-auto">
            <RecentRooms onJoinRoom={rejoinRoom} />
          </div>
        </div>
      </div>

      <footer className="relative z-10 mt-auto border-t border-white/[0.06] backdrop-blur-xl bg-[#0B0D10]/40 py-5">
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
          <span className="w-1 h-1 rounded-full bg-[#78B4FF] animate-pulse" />
          SyncScreen — synchronized watch parties
        </div>
      </footer>

      {pendingRoomId && (
        <JoinNameModal
          roomId={pendingRoomId}
          onCancel={() => { setPendingRoomId(null); setJoinCode(""); }}
          onConfirm={confirmJoinRoom}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleQueue — glass variant
// ---------------------------------------------------------------------------
function CollapsibleQueue({
  queue,
  canControl,
  onPlayNext,
  onRemove,
}: {
  queue: PlaylistItem[];
  canControl: boolean;
  onPlayNext: () => void;
  onRemove: (itemId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass-subtle border-t border-white/10 shrink-0 overflow-hidden relative">
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent pointer-events-none" />
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors relative"
      >
 <div className="w-7 h-7 flex items-center justify-center">
          <ListVideo className="w-3.5 h-3.5" />
        </div>
        <span className="font-semibold tracking-tight">Queue</span>
        <span className="text-[10px] font-mono glass no-border rounded-full px-2 py-1">{queue.length}</span>
 <span className="ml-auto w-7 h-7 flex items-center justify-center">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden relative"
          >
            <div className="px-4 pb-4 space-y-2 max-h-48 overflow-y-auto">
              {queue.length === 0 ? (
                <p className="text-[11px] text-muted-foreground text-center py-4 glass rounded-xl">Queue is empty — add videos from the bar above</p>
              ) : (
                queue.map((item, idx) => (
                  <div key={item.id} className="flex items-center gap-3 p-2.5 rounded-xl glass no-border hover:glass-strong text-xs transition-all group">
 <span className="text-[10px] font-mono text-muted-foreground w-6 h-6 flex items-center justify-center shrink-0">{idx + 1}</span>
                    <span className="flex-1 truncate font-medium">{item.title}</span>
                    {canControl && idx === 0 && (
 <button onClick={onPlayNext} className="w-7 h-7 flex items-center justify-center">
                        <Play className="w-3 h-3 fill-current" />
                      </button>
                    )}
 <button onClick={() => onRemove(item.id)} className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Join-name modal — ultra glass
// ---------------------------------------------------------------------------
function JoinNameModal({
  roomId,
  onCancel,
  onConfirm,
}: {
  roomId: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState("");
  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => {
      if (!active) return;
      const p = getProfile();
      if (p?.name) setName(p.name);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onCancel(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50" onClick={onCancel}>
      <div aria-hidden className="atmosphere" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
        className="glass-primary glass-highlight-edge rounded-3xl p-6 w-full max-w-sm relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#78B4FF]/12">
              <Users className="w-4 h-4 text-[#78B4FF]" />
            </span>
            Join Room
          </h3>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Enter a display name to join
          <span className="font-mono text-[#78B4FF] ml-1.5">{roomId}</span>.
        </p>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && name.trim() && onConfirm(name)}
          placeholder="Your display name"
          maxLength={40}
          className="w-full glass-input rounded-xl px-3.5 py-3 text-sm outline-none mb-4"
        />
        <button
          onClick={() => name.trim() && onConfirm(name)}
          disabled={!name.trim()}
          className="w-full glass-button-primary text-sm font-semibold py-3 rounded-xl disabled:opacity-40 transition-all"
        >
          Join Room
        </button>
      </motion.div>
    </div>
  );
}

// ===========================================================================
// ROOM VIEW — glass studio
// ===========================================================================
function RoomView({ session, onLeave }: { session: RoomSession; onLeave: () => void }) {
  const { roomId, userId, userName: initialName } = session;
  const [joined, setJoined] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [videoState, setVideoState] = useState<VideoState>({
    url: "", type: "direct", playing: false, currentTime: 0, playbackRate: 1, updatedAt: 0, updatedBy: "",
  });
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [hostId, setHostId] = useState(session.userId);
  const [hostOnly, setHostOnly] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [roomTitle, setRoomTitle] = useState("Watch Party Room");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [queue, setQueue] = useState<PlaylistItem[]>([]);
  const [bookmarks, setBookmarks] = useState<VideoBookmark[]>([]);
  const [subtitles, setSubtitles] = useState<SubtitleTrack | null>(null);
  const [activePoll, setActivePoll] = useState<RoomPoll | null>(null);
  const [floatingReactions, setFloatingReactions] = useState<FloatingReactionItem[]>([]);
  const [reactionBurst, setReactionBurst] = useState<{ icon: ReactionIcon; count: number; key: number } | null>(null);
  const reactionCountsRef = useRef<Record<string, number>>({});
  const burstTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [kicked, setKicked] = useState(false);
  const [pingMs, setPingMs] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [typers, setTypers] = useState<{ userId: string; userName: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [comments, setComments] = useState<TimeComment[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [customReactions, setCustomReactions] = useState<CustomReaction[]>([]);
  const [analytics, setAnalytics] = useState<RoomAnalytics | null>(null);
  const [playerTime, setPlayerTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [countdownEndsAt, setCountdownEndsAt] = useState<number | null>(null);
  const [chatHeight, setChatHeight] = useState(340);
  const [isMobile, setIsMobile] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [remoteScreenShares, setRemoteScreenShares] = useState<{ userId: string; stream: MediaStream }[]>([]);
  const screenShareRef = useRef<ScreenShareService | null>(null);
  const remoteScreenVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const [screenShareViewMode, setScreenShareViewMode] = useState<"single" | "split">("single");
  const [ssMuted, setSsMuted] = useState<Record<string, boolean>>({});
  const [ssVolume, setSsVolume] = useState<Record<string, number>>({});
  const [ssFullscreenId, setSsFullscreenId] = useState<string | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check(); window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const resizeRef = useRef<{ startY: number; startHeight: number } | null>(null);
  function startResize(e: React.PointerEvent) {
    e.preventDefault();
    const clientY = e.clientY;
    resizeRef.current = { startY: clientY, startHeight: chatHeight };
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    document.body.style.touchAction = "none";
    const onMove = (ev: PointerEvent) => {
      if (!resizeRef.current) return;
      ev.preventDefault();
      const delta = resizeRef.current.startY - ev.clientY;
      const newHeight = Math.max(160, Math.min(window.innerHeight - 180, resizeRef.current.startHeight + delta));
      setChatHeight(newHeight);
    };
    const onUp = () => {
      resizeRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.touchAction = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
  }

  const roomStoreRef = useRef<RoomStore | null>(null);
  const lastPingRef = useRef<number>(Date.now());
  const localUpdateRef = useRef<number>(0);
  const lastTypingSentRef = useRef<number>(0);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<any>({});
  stateRef.current = {
    videoState, participants, queue, bookmarks, subtitles, activePoll,
    comments, history, customReactions, analytics,
    hostId, hostOnly, isLocked, roomTitle,
  };
  const send = useCallback((event: RoomEvent) => { roomStoreRef.current?.send(event); }, []);
  const isHost = hostId === userId;
  const canControl = !hostOnly || isHost;

  const handleEvent = useCallback((event: RoomEvent) => {
    switch (event.type) {
      case "init": {
        const d = event.data as any;
        setVideoState(d.videoState); setParticipants(d.participants); setQueue(d.queue);
        setBookmarks(d.bookmarks); setSubtitles(d.subtitles); setActivePoll(d.activePoll);
        setHostOnly(d.hostOnly); setIsLocked(d.isLocked); setRoomTitle(d.title);
        if (d.comments) setComments(d.comments); if (d.history) setHistory(d.history);
        if (d.customReactions) setCustomReactions(d.customReactions); if (d.analytics) setAnalytics(d.analytics);
        // Admin persistence on rejoin: if the DB's admin_id (returned by
        // RoomStore.loadStateFromDB as `adminId`) matches our userId, we are
        // the room's creator — force hostId to our userId so isHost becomes
        // true. We also cross-check against the admin_id we stored in
        // sessionStorage at create time, so a stale/incorrect DB value can't
        // accidentally grant host to a non-admin. Either check passing is
        // sufficient; both are intentionally permissive for the room creator.
        const dbAdminId = (d.adminId as string | undefined) || (d.hostId as string | undefined) || "";
        const sessionAdminId = (typeof window !== "undefined" && sessionStorage.getItem(`room_${roomId}_adminId`)) || session.adminId || "";
        const isAdminFromDb = !!dbAdminId && dbAdminId === userId;
        const isAdminFromSession = !!sessionAdminId && sessionAdminId === userId;
        if (isAdminFromDb || isAdminFromSession) {
          setHostId(userId);
        } else {
          setHostId(d.hostId);
        }
        break;
      }
      case "video_state": {
        const d = event.data as VideoState;
        if (d.updatedAt === localUpdateRef.current) break;
        setVideoState(d); break;
      }
      case "chat": {
        const d = event.data as ChatMessage;
        setMessages((prev) => [...prev, d]);
        setAnalytics((prev) => prev ? { ...prev, totalMessages: prev.totalMessages + 1 } : prev);
        break;
      }
      case "system": {
        const d = event.data as { message: string };
        setMessages((prev) => [...prev, { id: `sys_${event.timestamp}`, userId: "", userName: "", content: d.message, type: "system", createdAt: new Date(event.timestamp).toISOString() }]);
        break;
      }
      case "participants":
      case "participant_progress": {
        const d = event.data as { participants: Participant[] };
        if (d.participants) setParticipants(d.participants); break;
      }
      case "settings": {
        const d = event.data as { hostOnly: boolean; isLocked: boolean; title: string };
        setHostOnly(d.hostOnly); setIsLocked(d.isLocked); setRoomTitle(d.title); break;
      }
      case "host_change": { const d = event.data as { newHostId: string }; setHostId(d.newHostId); break; }
      case "queue": { const d = event.data as { queue: PlaylistItem[] }; setQueue(d.queue); break; }
      case "bookmarks": { const d = event.data as { bookmarks: VideoBookmark[] }; setBookmarks(d.bookmarks); break; }
      case "subtitles": { const d = event.data as { subtitles: SubtitleTrack | null }; setSubtitles(d.subtitles); break; }
      case "poll": { const d = event.data as { poll: RoomPoll | null }; setActivePoll(d.poll); break; }
      case "reaction": {
        const d = event.data as { id: string; icon: ReactionIcon; customUrl?: string; userName: string };
        setFloatingReactions((prev) => [...prev, { id: d.id, icon: d.icon, customUrl: d.customUrl, userName: d.userName }]);
        setTimeout(() => { setFloatingReactions((prev) => prev.filter((r) => r.id !== d.id)); }, 3000);
        const iconKey = d.customUrl || d.icon;
        reactionCountsRef.current[iconKey] = (reactionCountsRef.current[iconKey] || 0) + 1;
        const total = reactionCountsRef.current[iconKey];
        setReactionBurst({ icon: d.icon, count: total, key: Date.now() });
        if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
        burstTimeoutRef.current = setTimeout(() => {
          setReactionBurst(null);
          // Reset the count to 0 so the next burst starts fresh
          reactionCountsRef.current[iconKey] = 0;
        }, 3000);
        setAnalytics((prev) => prev ? { ...prev, totalReactions: prev.totalReactions + 1 } : prev);
        break;
      }
      case "kick": {
        const d = event.data as { targetUserId: string };
        if (d.targetUserId === userId) { setKicked(true); roomStoreRef.current?.disconnect(); }
        break;
      }
      case "ping": { lastPingRef.current = Date.now(); break; }
      case "typing": {
        const d = event.data as { userId: string; userName: string };
        if (d.userId === userId) break;
        setTypers((prev) => {
          const filtered = prev.filter((t) => t.userId !== d.userId);
          return [...filtered, { userId: d.userId, userName: d.userName }];
        });
        setTimeout(() => { setTypers((prev) => prev.filter((t) => t.userId !== d.userId)); }, 4000);
        break;
      }
      case "comments": { const d = event.data as { comments: TimeComment[] }; setComments(d.comments || []); break; }
      case "countdown": { const d = event.data as { endsAt: number }; setCountdownEndsAt(d.endsAt); break; }
      case "history": { const d = event.data as { history: HistoryEntry[] }; setHistory(d.history || []); break; }
      case "custom_reactions": { const d = event.data as { customReactions: CustomReaction[] }; setCustomReactions(d.customReactions || []); break; }
      case "analytics": { const d = event.data as { analytics: RoomAnalytics }; setAnalytics(d.analytics); break; }
    }
  }, [userId, roomId, session.adminId]);

  useEffect(() => {
    setChatLoading(true);
    RoomStore.loadChatHistory(roomId).then((rows) => {
      if (rows && rows.length) setMessages(rows);
      setChatLoading(false);
    });
    RoomStore.loadComments(roomId).then((commentRows) => {
      if (commentRows && commentRows.length) {
        setComments((prev) => {
          const ids = new Set(commentRows.map((r) => r.id));
          const extra = prev.filter((c) => !ids.has(c.id));
          return [...commentRows, ...extra].sort((a, b) => a.time - b.time);
        });
      }
    });
  }, [roomId]);

  useEffect(() => {
    const store = new RoomStore(roomId, userId, initialName);
    roomStoreRef.current = store;
    setJoined(true); setReconnecting(false); lastPingRef.current = Date.now();
    const unsub = store.onEvent((event) => { handleEvent(event); if (event.type === "ping") lastPingRef.current = Date.now(); });
    store.onStateRequest(() => {
      const room = stateRef.current;
      return {
        videoState: room.videoState, participants: room.participants, queue: room.queue,
        bookmarks: room.bookmarks, subtitles: room.subtitles, activePoll: room.activePoll,
        hostId: room.hostId, hostOnly: room.hostOnly, isLocked: room.isLocked, title: room.roomTitle,
        comments: room.comments, history: room.history, customReactions: room.customReactions, analytics: room.analytics,
      } as any;
    });
    return () => { unsub(); store.disconnect(); roomStoreRef.current = null; };
  }, [roomId, userId, initialName, handleEvent]);

  useEffect(() => {
    const ss = new ScreenShareService(roomId, userId);
    screenShareRef.current = ss;
    ss.setHandlers(
      (remoteUserId, stream) => {
        setRemoteScreenShares((prev) => {
          const filtered = prev.filter((s) => s.userId !== remoteUserId);
          return [...filtered, { userId: remoteUserId, stream }];
        });
      },
      (remoteUserId) => { setRemoteScreenShares((prev) => prev.filter((s) => s.userId !== remoteUserId)); }
    );
    ss.init();
    return () => { ss.disconnect(); screenShareRef.current = null; };
  }, [roomId, userId]);

  async function handleStartScreenShare() {
    if (!screenShareRef.current) return;
    const ok = await screenShareRef.current.startSharing();
    if (ok) { setIsScreenSharing(true); toast.success("Screen sharing started"); }
    else toast.error("Could not start screen sharing");
  }
  function handleStopScreenShare() { screenShareRef.current?.stopSharing(); setIsScreenSharing(false); toast("Screen sharing stopped"); }

  function toggleSsMute(sharerId: string) {
    setSsMuted((prev) => {
      const next = { ...prev, [sharerId]: !prev[sharerId] };
      const videoEl = remoteScreenVideoRefs.current.get(sharerId);
      if (videoEl) {
        videoEl.muted = !!next[sharerId];
        if (!next[sharerId] && videoEl.volume === 0) videoEl.volume = ssVolume[sharerId] ?? 1;
      }
      return next;
    });
  }
  function setSsVolumeFor(sharerId: string, v: number) {
    const clamped = Math.max(0, Math.min(1, v));
    setSsVolume((prev) => ({ ...prev, [sharerId]: clamped }));
    const videoEl = remoteScreenVideoRefs.current.get(sharerId);
    if (videoEl) {
      videoEl.volume = clamped;
      if (clamped > 0 && videoEl.muted) { videoEl.muted = false; setSsMuted((prev) => ({ ...prev, [sharerId]: false })); }
    }
  }
  function toggleSsFullscreen(sharerId: string) {
    const videoEl = remoteScreenVideoRefs.current.get(sharerId);
    if (!videoEl) return;
    const container = videoEl.closest(".ss-container") as HTMLElement | null;
    const target = container || videoEl;
    if (!document.fullscreenElement) {
      const req = (target as any).requestFullscreen?.bind(target) || (target as any).webkitRequestFullscreen?.bind(target);
      if (req) req().then(() => setSsFullscreenId(sharerId)).catch(() => {});
    } else {
      const exit = (document as any).exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
      if (exit) exit().then(() => setSsFullscreenId(null)).catch(() => {});
    }
  }
  function handleSsResync(sharerId: string) {
    screenShareRef.current?.resync(sharerId);
    toast("Re-syncing screen share…", { description: "Asking the sharer for a fresh stream." });
  }
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) setSsFullscreenId(null);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange as any);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange as any);
    };
  }, []);
  useEffect(() => {
    remoteScreenShares.forEach(({ userId, stream }) => {
      const videoEl = remoteScreenVideoRefs.current.get(userId);
      if (videoEl && videoEl.srcObject !== stream) {
        videoEl.srcObject = stream;
        const muted = ssMuted[userId] ?? true;
        const vol = ssVolume[userId] ?? 1;
        videoEl.muted = muted; videoEl.volume = vol;
        videoEl.play().catch(() => {});
      }
    });
  }, [remoteScreenShares]);

  useEffect(() => {
    const id = setInterval(() => {
      const sentAt = Date.now();
      const unsub = roomStoreRef.current?.onEvent((event) => {
        if (event.type === "__pong") { setPingMs(Math.min(Date.now() - sentAt, 9999)); lastPingRef.current = Date.now(); unsub?.(); }
      });
      roomStoreRef.current?.send({ type: "__ping" as any, data: { sentAt }, timestamp: sentAt });
      setTimeout(() => unsub?.(), 3000);
    }, 10000);
    return () => clearInterval(id);
  }, []);

  function handlePlay(t: number) {
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState = { ...stateRef.current.videoState, playing: true, currentTime: t, updatedAt, updatedBy: userId };
    setVideoState(newState); send({ type: "video_state", data: newState, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} resumed playback`, userId, userName: initialName }, timestamp: Date.now() });
  }
  function handlePause(t: number) {
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState = { ...stateRef.current.videoState, playing: false, currentTime: t, updatedAt, updatedBy: userId };
    setVideoState(newState); send({ type: "video_state", data: newState, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} paused`, userId, userName: initialName }, timestamp: Date.now() });
  }
  // Force Pause — host-only moderator control. Pauses the video for everyone.
  function handleForcePause() {
    if (!isHost) return;
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState = { ...stateRef.current.videoState, playing: false, updatedAt, updatedBy: userId };
    setVideoState(newState); send({ type: "video_state", data: newState, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} force-paused the video for everyone`, userId, userName: initialName }, timestamp: Date.now() });
    toast("Video force-paused for everyone", { description: "All viewers have been paused." });
  }
  function handleSeek(t: number) {
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState = { ...stateRef.current.videoState, currentTime: t, updatedAt, updatedBy: userId };
    setVideoState(newState); send({ type: "video_state", data: newState, timestamp: Date.now() });
  }
  function handleSpeedChange(r: number) {
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    setVideoState((v) => ({ ...v, playbackRate: r, updatedAt, updatedBy: userId }));
    send({ type: "video_state", data: { ...stateRef.current.videoState, playbackRate: r, updatedAt, updatedBy: userId }, timestamp: Date.now() });
  }
  function handleLoadVideo(url: string, type: VideoType) {
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState: VideoState = { url, type, playing: false, currentTime: 0, playbackRate: 1, updatedAt, updatedBy: userId };
    setVideoState(newState); send({ type: "video_state", data: newState, timestamp: Date.now() });
    const entry: HistoryEntry = { id: `hist_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, url, type, title: url.slice(0, 120), playedBy: userId, playedByName: initialName, playedAt: Date.now() };
    const newHistory = [entry, ...stateRef.current.history].slice(0, 50);
    setHistory(newHistory); send({ type: "history", data: { history: newHistory }, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} loaded a new video`, userId, userName: initialName }, timestamp: Date.now() });
    toast.success("Video loaded", { description: `Source: ${type}` });
  }
  const lastMessageTimeRef = useRef<number>(0);
  function handleSend(content: string) {
    const now = Date.now();
    if (now - lastMessageTimeRef.current < 1000) { toast.error("Please wait a moment before sending another message"); return; }
    lastMessageTimeRef.current = now;
    const msg: ChatMessage = { id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, userId, userName: initialName, content, type: "chat", createdAt: new Date().toISOString() };
    RoomStore.saveChatMessage(roomId, msg).catch(() => {});
    send({ type: "chat", data: msg as any, timestamp: Date.now() });
  }
  function handleReaction(icon: ReactionIcon, customUrl?: string) {
    send({ type: "reaction", data: { id: `react_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, icon, customUrl, userId, userName: initialName, timestamp: Date.now() } as any, timestamp: Date.now() });
  }
  function handleAddCustomReaction(url: string, label: string) {
    const reaction: CustomReaction = { id: `cr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, url, label, addedBy: userId, addedByName: initialName, addedAt: Date.now() };
    const newList = [...stateRef.current.customReactions, reaction].slice(0, 12);
    setCustomReactions(newList); send({ type: "custom_reactions", data: { customReactions: newList }, timestamp: Date.now() });
    toast.success("Custom reaction added", { description: label });
  }
  function handleRemoveCustomReaction(reactionId: string) {
    const newList = stateRef.current.customReactions.filter((r: CustomReaction) => r.id !== reactionId);
    setCustomReactions(newList); send({ type: "custom_reactions", data: { customReactions: newList }, timestamp: Date.now() });
  }
  function handleTyping() {
    const now = Date.now(); if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now; send({ type: "typing", data: { userId, userName: initialName }, timestamp: now });
  }
  function handleAddComment(time: number, content: string) {
    const comment: TimeComment = { id: `cmt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, time, content: content.slice(0, 200), createdBy: userId, createdByName: initialName, createdAt: Date.now() };
    RoomStore.saveComment(roomId, comment).catch(() => {});
    const newComments = [...stateRef.current.comments, comment].sort((a: TimeComment, b: TimeComment) => a.time - b.time);
    setComments(newComments); send({ type: "comments", data: { comments: newComments }, timestamp: Date.now() });
  }
  function handleRemoveComment(commentId: string) {
    RoomStore.deleteComment(roomId, commentId).catch(() => {});
    const newComments = stateRef.current.comments.filter((c: TimeComment) => c.id !== commentId);
    setComments(newComments); send({ type: "comments", data: { comments: newComments }, timestamp: Date.now() });
  }
  function handlePinCommentToChat(time: number, content: string, authorName: string) {
    const msg = `[${formatTime(time)}] ${authorName}: ${content}`;
    handleSend(msg); toast.success("Pinned to chat", { description: "Comment echoed to the chat panel." });
  }
  function handleAddToQueue(url: string, title?: string) {
    const item: PlaylistItem = { id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, url, type: "youtube" as VideoType, title: (title || url).slice(0, 120), addedBy: userId, addedByName: initialName };
    const newQueue = [...stateRef.current.queue, item];
    setQueue(newQueue); send({ type: "queue", data: { queue: newQueue }, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} added "${item.title}" to the queue`, userId, userName: initialName }, timestamp: Date.now() });
    toast.success("Added to queue", { description: title || url });
  }
  function handleRemoveFromQueue(itemId: string) {
    const newQueue = stateRef.current.queue.filter((i: PlaylistItem) => i.id !== itemId);
    setQueue(newQueue); send({ type: "queue", data: { queue: newQueue }, timestamp: Date.now() });
  }
  function handleReorderQueue(orderedIds: string[]) {
    const map = new Map(stateRef.current.queue.map((i: PlaylistItem) => [i.id, i]));
    const newQueue = orderedIds.map((id) => map.get(id)).filter(Boolean) as PlaylistItem[];
    setQueue(newQueue); send({ type: "queue", data: { queue: newQueue }, timestamp: Date.now() });
  }
  function handlePlayNextQueue() {
    if (stateRef.current.queue.length === 0) return;
    const nextItem = stateRef.current.queue[0]; const rest = stateRef.current.queue.slice(1);
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState: VideoState = { url: nextItem.url, type: nextItem.type, playing: true, currentTime: 0, playbackRate: 1, updatedAt, updatedBy: userId };
    setVideoState(newState); setQueue(rest);
    send({ type: "video_state", data: newState, timestamp: Date.now() });
    send({ type: "queue", data: { queue: rest }, timestamp: Date.now() });
    send({ type: "system", data: { message: `Playing next: "${nextItem.title}"`, userId, userName: initialName }, timestamp: Date.now() });
  }
  // Play a specific queue item by ID — keeps the queue intact, just moves
  // the playback head to the clicked item. The item is removed from the queue.
  function handlePlayQueueItem(itemId: string) {
    const item = stateRef.current.queue.find((q) => q.id === itemId);
    if (!item) return;
    const rest = stateRef.current.queue.filter((q) => q.id !== itemId);
    const updatedAt = Date.now(); localUpdateRef.current = updatedAt;
    const newState: VideoState = { url: item.url, type: item.type, playing: true, currentTime: 0, playbackRate: 1, updatedAt, updatedBy: userId };
    setVideoState(newState); setQueue(rest);
    send({ type: "video_state", data: newState, timestamp: Date.now() });
    send({ type: "queue", data: { queue: rest }, timestamp: Date.now() });
    send({ type: "system", data: { message: `Playing from queue: "${item.title}"`, userId, userName: initialName }, timestamp: Date.now() });
  }
  function handleCreatePoll(question: string, options: string[]) {
    const poll: RoomPoll = { id: `poll_${Date.now()}`, question, options: options.map((text, idx) => ({ id: `opt_${idx}`, text, votes: [] })), createdBy: userId, createdByName: initialName, active: true, createdAt: Date.now() };
    setActivePoll(poll); send({ type: "poll", data: { poll }, timestamp: Date.now() });
    send({ type: "system", data: { message: `${initialName} started a poll: "${question}"`, userId, userName: initialName }, timestamp: Date.now() });
    toast.success("Poll launched", { description: question });
  }
  function handleVotePoll(optionId: string) {
    if (!stateRef.current.activePoll || !stateRef.current.activePoll.active) return;
    const poll = { ...stateRef.current.activePoll };
    poll.options = poll.options.map((opt: any) => ({ ...opt, votes: opt.votes.filter((uid: string) => uid !== userId) }));
    const target = poll.options.find((opt: any) => opt.id === optionId);
    if (target) target.votes.push(userId);
    setActivePoll(poll); send({ type: "poll", data: { poll }, timestamp: Date.now() });
  }
  function handleClosePoll() {
    if (!stateRef.current.activePoll) return;
    const poll = { ...stateRef.current.activePoll, active: false };
    setActivePoll(poll); send({ type: "poll", data: { poll }, timestamp: Date.now() }); toast("Poll closed");
  }
  function handleProgressPing(currentTime: number, playbackState: "playing" | "paused" | "buffering") {
    send({ type: "participant_progress", data: { userId, currentTime, playbackState, participants: stateRef.current.participants }, timestamp: Date.now() });
  }
  function handleTimeUpdate(currentTime: number, duration: number) { setPlayerTime(currentTime); setPlayerDuration(duration); }
  function handleStartCountdown() {
    send({ type: "countdown", data: { endsAt: Date.now() + 3000, durationMs: 3000 }, timestamp: Date.now() });
    toast("Starting synced countdown", { description: "3… 2… 1… Play!" });
  }
  function handleAddBookmark(time: number, label: string) {
    const bookmark: VideoBookmark = { id: `bm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, time, label: label.slice(0, 60), createdBy: userId, createdByName: initialName, createdAt: Date.now() };
    const newBookmarks = [...stateRef.current.bookmarks, bookmark].sort((a: VideoBookmark, b: VideoBookmark) => a.time - b.time);
    setBookmarks(newBookmarks); send({ type: "bookmarks", data: { bookmarks: newBookmarks }, timestamp: Date.now() });
    toast.success("Bookmark saved", { description: label });
  }
  function handleToggleHostOnly() {
    const newHostOnly = !hostOnly;
    send({ type: "settings", data: { hostOnly: newHostOnly, isLocked, title: roomTitle }, timestamp: Date.now() });
    toast(newHostOnly ? "Host-only controls ON" : "Controls open for everyone");
  }
  function handleToggleLock() {
    const newLocked = !isLocked;
    send({ type: "settings", data: { hostOnly, isLocked: newLocked, title: roomTitle }, timestamp: Date.now() });
    toast(newLocked ? "Room locked to new joiners" : "Room unlocked");
  }
  function handleRenameRoom(newTitle: string) {
    send({ type: "settings", data: { hostOnly, isLocked, title: newTitle }, timestamp: Date.now() });
    toast.success("Room renamed", { description: newTitle });
  }
  function handleTransferHost(targetUserId: string) {
    const target = participants.find((p) => p.id === targetUserId);
    send({ type: "host_change", data: { newHostId: targetUserId, newHostName: target?.name || "User" }, timestamp: Date.now() });
    toast.success("Host role transferred");
  }
  function handleKickParticipant(targetUserId: string) {
    const target = participants.find((p) => p.id === targetUserId);
    send({ type: "kick", data: { targetUserId, targetName: target?.name || "Participant" }, timestamp: Date.now() });
  }

  function handleSubtitleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const track = parseSubtitleFile(text, file.name.replace(/\.[^.]+$/, ""));
      setSubtitles(track); send({ type: "subtitles", data: { subtitles: track }, timestamp: Date.now() });
      send({ type: "system", data: { message: `${initialName} loaded synchronized subtitles`, userId, userName: initialName }, timestamp: Date.now() });
    };
    reader.readAsText(file); e.target.value = "";
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
      switch (e.key) {
        case " ":
        case "k":
          if (canControl) {
            e.preventDefault();
            if (videoState.playing) handlePause(videoState.currentTime);
            else handlePlay(videoState.currentTime);
          }
          break;
        case "ArrowLeft":
          if (canControl) { e.preventDefault(); handleSeek(Math.max(0, videoState.currentTime - 10)); }
          break;
        case "ArrowRight":
          if (canControl) { e.preventDefault(); handleSeek(videoState.currentTime + 10); }
          break;
        case "?": e.preventDefault(); setShowShortcuts((s) => !s); break;
        default:
          if (/^[1-8]$/.test(e.key)) {
            e.preventDefault();
            const idx = parseInt(e.key, 10) - 1;
            const icon = REACTION_ORDER[idx];
            if (icon) handleReaction(icon);
          }
          if (e.shiftKey && /^[1-9]$/.test(e.key)) {
            e.preventDefault();
            const idx = parseInt(e.key, 10) - 1;
            const cr = customReactions[idx];
            if (cr) handleReaction("heart", cr.url);
          }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canControl, videoState, customReactions]);

  const [countdownNumber, setCountdownNumber] = useState<number | null>(null);
  useEffect(() => {
    if (countdownEndsAt === null) return;
    let active = true;
    const tick = () => {
      if (!active) return;
      const remaining = Math.ceil((countdownEndsAt - Date.now()) / 1000);
      if (remaining <= 0) {
        setCountdownNumber(null); setCountdownEndsAt(null);
        handlePlay(videoState.currentTime); return;
      }
      setCountdownNumber(remaining);
      raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    return () => { active = false; cancelAnimationFrame(raf); };
  }, [countdownEndsAt]);

  if (kicked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        <div aria-hidden className="atmosphere" />
        <div className="text-center max-w-sm glass-primary glass-highlight-edge rounded-3xl p-8 relative">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-2xl glass-tertiary">
            <X className="w-5 h-5 text-muted-foreground" />
          </div>
          <h2 className="text-base font-semibold mb-2">You were removed</h2>
          <p className="text-xs text-muted-foreground mb-6">The host removed you from this room.</p>
          <button onClick={onLeave} className="px-5 py-2.5 glass-button-primary text-sm font-medium rounded-xl">Back to home</button>
        </div>
      </div>
    );
  }

  // Loading splash — prevents the room controls from flickering into view
  // before the room state is fully loaded on page refresh.
  if (!joined) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div aria-hidden className="atmosphere" />
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-[#78B4FF] rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground">Joining room…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col relative text-foreground overflow-hidden">
      {/* Static atmospheric gradients behind the room — barely visible through glass */}
      <div aria-hidden className="atmosphere" />

      {/* Unified floating header — top controls + URL bar as ONE cohesive glass strip.
          blur 20px, opacity 20%, subtle bottom highlight. Icons sit directly on glass. */}
      <div className="shrink-0 sticky top-0 z-30 bg-white/[0.02] backdrop-blur-[20px] saturate-[180%] border-b border-white/[0.06] header-highlight">
        {/* Top row — live controls, icons directly on glass */}
        <header className="flex items-center justify-between gap-2 px-3 sm:px-4 h-[50px]">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={onLeave} className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.04] shrink-0 transition-all">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="min-w-0 hidden sm:block">
                <div className="text-[13px] font-semibold tracking-tight truncate flex items-center gap-2">
                  {roomTitle}
                  {reconnecting ? (
                    <span className="text-[10px] text-muted-foreground font-normal flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> reconnecting
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium ${pingMs < 200 ? "text-[#78B4FF]" : "text-muted-foreground"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${pingMs < 200 ? "bg-[#78B4FF] animate-pulse shadow-[0_0_6px_currentColor]" : pingMs < 500 ? "bg-amber-400" : "bg-red-400"}`} />
                      {pingMs < 200 ? "live" : pingMs < 500 ? "slow" : "laggy"}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground/70 font-mono">{roomId}</div>
              </div>
              <div className="sm:hidden flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${pingMs < 200 ? "bg-[#78B4FF] animate-pulse" : pingMs < 500 ? "bg-amber-400" : "bg-red-400"}`} />
                <span className="text-[9px] font-mono text-muted-foreground">{pingMs > 0 ? `${pingMs}ms` : "live"}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-1.5">
            <ThemeToggle />
            {/* Force Pause — host-only moderator control */}
            {isHost && videoState.url && (
              <button onClick={handleForcePause} disabled={!videoState.playing} className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-[#78B4FF] hover:bg-white/[0.04] disabled:opacity-30 disabled:hover:text-muted-foreground transition-all" title="Pause for everyone" aria-label="Pause for everyone">
                <Pause className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setShowSettings(true)} className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-all">
              <Settings className="w-4 h-4" />
            </button>
            <button onClick={() => setShowShortcuts(true)} className="w-8 h-8 hidden sm:flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-all">
              <HelpCircle className="w-4 h-4" />
            </button>
            <ParticipantBar
              participants={participants}
              isHost={isHost}
              hostOnly={hostOnly}
              isLocked={isLocked}
              roomTitle={roomTitle}
              hostId={hostId}
              currentUserId={userId}
              roomId={roomId}
              pingMs={pingMs}
              onToggleHostOnly={handleToggleHostOnly}
              onToggleLock={handleToggleLock}
              onRenameRoom={handleRenameRoom}
              onTransferHost={handleTransferHost}
              onKickParticipant={handleKickParticipant}
              onLeaveRoom={onLeave}
              onJumpToTime={canControl ? handleSeek : undefined}
            />
          </div>
        </header>

        {/* Subtle divider between rows */}
        {canControl && (
          <div className="hidden sm:flex items-center gap-2 px-4 py-1 text-[11px] border-t border-white/[0.04]">
            <label className="flex items-center gap-1.5 cursor-pointer px-2.5 py-1 rounded-full hover:bg-white/[0.04] transition-all">
              <Upload className="w-3 h-3 text-muted-foreground" />
              <span className="hidden lg:inline text-muted-foreground">Upload subtitles</span>
              <span className="lg:hidden text-muted-foreground">Subtitles</span>
              <input type="file" accept=".srt,.vtt,text/vtt" onChange={handleSubtitleUpload} className="hidden" />
            </label>
            {subtitles && (
              <button
                onClick={() => {
                  setSubtitles(null);
                  send({ type: "subtitles", data: { subtitles: null }, timestamp: Date.now() });
                  send({ type: "system", data: { message: `${initialName} cleared subtitles`, userId, userName: initialName }, timestamp: Date.now() });
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors"
              >
                <Subtitles className="w-3 h-3" /> Clear
              </button>
            )}
            {videoState.url && !videoState.playing && (
              <button
                onClick={handleStartCountdown}
                className="flex items-center gap-1.5 ml-auto bg-[#78B4FF]/12 hover:bg-[#78B4FF]/20 text-[#78B4FF] px-3 py-1.5 rounded-full font-semibold text-xs transition-all"
              >
                <Zap className="w-3 h-3" /> Sync Start
              </button>
            )}
          </div>
        )}

        {/* URL bar — integrated controls on glass, no separate containers */}
        <div className="flex items-center border-t border-white/[0.04]">
          <div className="flex-1 min-w-0">
            <VideoUrlBar videoState={videoState} canControl={canControl} onOpenSearch={() => setShowSearch(true)} onLoadVideo={handleLoadVideo} />
          </div>
          <button
            onClick={isScreenSharing ? handleStopScreenShare : handleStartScreenShare}
            className={`h-11 px-3 sm:px-4 flex items-center gap-1.5 text-xs font-medium border-l border-white/[0.04] transition-all shrink-0 ${isScreenSharing ? "text-white bg-[#78B4FF]/15 hover:bg-[#78B4FF]/25" : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"}`}
          >
            {isScreenSharing ? <Square className="w-3.5 h-3.5 fill-current" /> : <ScreenShare className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{isScreenSharing ? "Stop" : "Share"}</span>
          </button>
        </div>
      </div>

      <main className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden relative z-10">
        {remoteScreenShares.length > 0 && (
          <div className="flex lg:hidden items-center justify-between gap-2 px-3 py-1.5 glass-tertiary border-b border-white/[0.06] text-[11px] shrink-0 z-20">
            <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
              <ScreenShare className="w-3 h-3 text-[#78B4FF] shrink-0" />
              <span className="truncate font-medium">{remoteScreenShares.length} screen share{remoteScreenShares.length > 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-0.5 p-0.5 rounded-full glass-tertiary shrink-0">
              <button onClick={() => setScreenShareViewMode("single")} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${screenShareViewMode === "single" ? "bg-white/[0.08] text-foreground" : "text-muted-foreground"}`}><RectangleHorizontal className="w-3 h-3" /> Single</button>
              <button onClick={() => setScreenShareViewMode("split")} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${screenShareViewMode === "split" ? "bg-white/[0.08] text-foreground" : "text-muted-foreground"}`}><PanelsTopBottom className="w-3 h-3" /> Split</button>
            </div>
          </div>
        )}

        {remoteScreenShares.length > 0 && screenShareViewMode === "split" && (
          <div className="hidden lg:flex lg:flex-col bg-black/40 backdrop-blur border-b border-white/[0.06] shrink-0 max-h-[36%] overflow-hidden w-full lg:w-auto">
            {remoteScreenShares.map(({ userId: sUserId }) => {
              const sharer = participants.find((p) => p.id === sUserId);
              const muted = ssMuted[sUserId] ?? true;
              const vol = ssVolume[sUserId] ?? 1;
              const isFs = ssFullscreenId === sUserId;
              return (
                <div key={sUserId} className="ss-container relative flex-1 min-h-0">
                  <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5 glass-primary px-2.5 py-1 rounded-full text-[10px] font-medium text-foreground">
                    <ScreenShare className="w-3 h-3" /> {sharer?.name || "Someone"}'s screen
                  </div>
                  <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
                    <div className="flex items-center glass-primary rounded-full h-8 px-3">
                      <input type="range" min={0} max={1} step={0.05} value={vol} onChange={(e) => setSsVolumeFor(sUserId, parseFloat(e.target.value))} className="w-16 h-1 accent-[#78B4FF] cursor-pointer" />
                    </div>
 <button onClick={() => toggleSsMute(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary text-foreground">{muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}</button>
 <button onClick={() => handleSsResync(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary text-foreground"><RefreshCw className="w-3.5 h-3.5" /></button>
 <button onClick={() => toggleSsFullscreen(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary text-foreground">{isFs ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}</button>
                  </div>
                  <video ref={(el) => { if (el) remoteScreenVideoRefs.current.set(sUserId, el); }} className="w-full h-full object-contain" autoPlay playsInline muted={muted} />
                </div>
              );
            })}
          </div>
        )}

        {/* Video section — full-bleed on mobile, normal layout on desktop */}
        <section className="flex flex-col min-w-0 flex-1 lg:flex-1 lg:min-h-0 lg:overflow-y-auto overflow-hidden relative">
          {/* Video container — absolute fills mobile (full-bleed), relative on desktop */}
          <div className="absolute inset-0 lg:relative lg:inset-auto lg:flex-1 flex items-center justify-center lg:p-3 lg:min-h-0 video-canvas-glow">
              {remoteScreenShares.length > 0 && screenShareViewMode === "single" ? (
                <div className="w-full h-full max-w-full flex flex-col">
                  {remoteScreenShares.map(({ userId: sUserId }) => {
                    const sharer = participants.find((p) => p.id === sUserId);
                    const muted = ssMuted[sUserId] ?? true;
                    const vol = ssVolume[sUserId] ?? 1;
                    const isFs = ssFullscreenId === sUserId;
                    return (
                      <div key={sUserId} className="ss-container relative flex-1 min-h-0 bg-black overflow-hidden">
                        <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5 glass-primary px-2.5 py-1 rounded-full text-[10px] font-medium">
                          <ScreenShare className="w-3 h-3" /> {sharer?.name || "Someone"}'s screen
                        </div>
                        <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
                          <div className="hidden sm:flex items-center glass-primary rounded-full h-8 px-3"><input type="range" min={0} max={1} step={0.05} value={vol} onChange={(e) => setSsVolumeFor(sUserId, parseFloat(e.target.value))} className="w-20 h-1 accent-[#78B4FF] cursor-pointer" /></div>
 <button onClick={() => toggleSsMute(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary">{muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}</button>
 <button onClick={() => handleSsResync(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary"><RefreshCw className="w-3.5 h-3.5" /></button>
 <button onClick={() => toggleSsFullscreen(sUserId)} className="w-8 h-8 flex items-center justify-center rounded-full glass-primary">{isFs ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}</button>
                        </div>
                        <video ref={(el) => { if (el) remoteScreenVideoRefs.current.set(sUserId, el); }} className="w-full h-full object-contain" autoPlay playsInline muted={muted} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full h-full max-w-full shrink-0 overflow-hidden">
                  <VideoPlayer videoState={videoState} canControl={canControl} participants={participants} currentUserId={userId} bookmarks={bookmarks} subtitles={subtitles} floatingReactions={floatingReactions} onPlay={handlePlay} onPause={handlePause} onSeek={handleSeek} onSpeedChange={handleSpeedChange} onAddBookmark={handleAddBookmark} onProgressPing={handleProgressPing} onTimeUpdate={handleTimeUpdate} />
                </div>
              )}
              <AnimatePresence>
                {countdownNumber !== null && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 flex items-center justify-center bg-black/65 backdrop-blur-sm z-40">
                    <motion.div key={countdownNumber} initial={{ scale: 1.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} transition={{ duration: 0.3, ease: "easeOut" }} className="text-center glass-primary rounded-3xl px-8 py-6">
                      <div className="text-7xl sm:text-8xl font-semibold text-foreground tabular-nums">{countdownNumber}</div>
                      <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-widest">Synced start</div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {reactionBurst && (
                  <motion.div key={reactionBurst.key} initial={{ scale: 0.4, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="absolute top-3 right-3 z-30 pointer-events-none flex items-center gap-1.5 glass-primary rounded-full pl-1.5 pr-3 py-1.5">
                    {(() => { const reaction = REACTION_ICONS[reactionBurst.icon]; return <span className="text-sm">{reaction?.emoji || "❤️"}</span>; })()}
                    <span className="text-xs font-mono font-semibold tabular-nums">{reactionBurst.count > 999 ? "999+" : reactionBurst.count}</span>
                  </motion.div>
                )}
              </AnimatePresence>
          </div>

          {/* Below-video panels — desktop only (lg:flex). Hidden on mobile to keep video as primary canvas. */}
          <div className="hidden lg:flex flex-col gap-3 p-3 pt-0">
            {videoState.url && (
              <div className="glass-secondary rounded-2xl overflow-hidden">
                <NowPlayingPreview roomId={roomId} url={videoState.url} type={videoState.type} />
              </div>
            )}
            <div className="glass-secondary rounded-2xl overflow-hidden">
              <CollapsibleQueue queue={queue} canControl={canControl} onPlayNext={handlePlayNextQueue} onRemove={handleRemoveFromQueue} />
            </div>
            <div className="glass-secondary rounded-2xl overflow-hidden">
              <HistoryPanel history={history} onLoadVideo={canControl ? (url, type, title) => handleLoadVideo(url, type) : undefined} />
            </div>
            <div className="glass-secondary rounded-2xl overflow-hidden">
              <AnalyticsPanel analytics={analytics} currentViewers={participants.length} />
            </div>
          </div>
        </section>

        {/* Chat — floating overlay on mobile (absolute bottom), sidebar on desktop (lg:relative) */}
        <aside
          className="absolute bottom-0 left-0 right-0 lg:relative lg:w-[380px] xl:w-[420px] lg:shrink-0 lg:flex-none lg:p-3 lg:pl-0 z-20 lg:z-auto flex flex-col"
          style={{ height: isMobile ? `${chatHeight}px` : undefined }}
        >
          {/* Mobile resize handle — on top of chat overlay */}
          <div className="h-3 shrink-0 cursor-row-resize flex items-center justify-center group touch-none lg:hidden" onPointerDown={startResize}>
            <div className="w-10 h-1 rounded-full bg-white/15 group-hover:bg-white/30 transition-colors" />
          </div>
          {/* Chat panel — floating translucent layer with ambient light leaking through */}
          <div className="flex-1 min-h-0 glass-secondary chat-ambient rounded-t-3xl lg:rounded-3xl overflow-hidden flex flex-col">
            <ChatPanel
              messages={messages} userId={userId} userName={initialName} isHost={isHost} canControl={canControl} hostOnly={hostOnly} isLocked={isLocked} roomTitle={roomTitle} roomId={roomId} pingMs={pingMs} queue={queue} activePoll={activePoll} participants={participants} hostId={hostId}
              onSend={handleSend} onReaction={handleReaction} onSeek={handleSeek} onAddToQueue={handleAddToQueue} onRemoveFromQueue={handleRemoveFromQueue} onPlayNextQueue={handlePlayNextQueue} onPlayQueueItem={canControl ? handlePlayQueueItem : undefined} onReorderQueue={handleReorderQueue} onCreatePoll={handleCreatePoll} onVotePoll={handleVotePoll} onClosePoll={handleClosePoll} onOpenSearch={() => setShowSearch(true)} typers={typers} onTyping={handleTyping} chatLoading={chatLoading} customReactions={customReactions} onAddCustomReaction={isHost ? handleAddCustomReaction : undefined} onRemoveCustomReaction={isHost ? handleRemoveCustomReaction : undefined} onCustomReaction={(url) => handleReaction("heart", url)}
            />
          </div>
        </aside>
      </main>

      <footer className="bg-transparent backdrop-blur-sm border-t border-white/[0.03] py-2 text-center text-[10px] text-muted-foreground/60 shrink-0 relative z-10 hidden lg:block">
        <span className="inline-flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-[#78B4FF] animate-pulse" />
          SyncScreen • {participants.length} viewer{participants.length !== 1 ? "s" : ""} • real-time sync
        </span>
      </footer>

      <YouTubeSearchModal isOpen={showSearch} canControl={canControl} onClose={() => setShowSearch(false)} onSelectVideo={handleLoadVideo} onAddToQueue={handleAddToQueue} />
      <RoomSettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} isHost={isHost} roomTitle={roomTitle} hostOnly={hostOnly} isLocked={isLocked} participantCount={participants.length} roomId={roomId} onToggleHostOnly={handleToggleHostOnly} onToggleLock={handleToggleLock} onRenameRoom={handleRenameRoom} />

      {showShortcuts && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-md flex items-center justify-center p-4 z-50" onClick={() => setShowShortcuts(false)}>
          <div className="glass-primary glass-highlight-edge rounded-3xl p-6 w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 relative">
              <h3 className="text-sm font-semibold flex items-center gap-2"><HelpCircle className="w-4 h-4 text-[#78B4FF]" /> Keyboard Shortcuts</h3>
 <button onClick={() => setShowShortcuts(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/[0.04]"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1.5 text-xs relative">
              {[
                { key: "Space / K", desc: "Play / Pause (host)" },
                { key: "← / →", desc: "Seek 10s back / forward" },
                { key: "1 – 8", desc: "Send built-in reactions" },
                { key: "Shift + 1-9", desc: "Send custom reactions" },
                { key: "?", desc: "Toggle this help" },
              ].map((s) => (
                <div key={s.key} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-white/[0.04]">
                  <span className="text-muted-foreground">{s.desc}</span>
                  <kbd className="font-mono text-[10px] text-[#78B4FF]">{s.key}</kbd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen chat overlay — floating, low-opacity, auto-fade.
          Only visible when video player is in fullscreen mode. */}
      {isFullscreen && <FullscreenChatOverlay messages={messages} />}
    </div>
  );
}
